import React, { useState } from "react";
import { FaMapMarkerAlt, FaIndustry, FaBuilding, FaEnvelope, FaPhone } from "react-icons/fa";
import FormField, { inputClass, Honeypot, FormStatus, SubmitButton } from "./forms/FormField";
import { validateContact } from "../lib/formValidation";
import { submitForm } from "../lib/submitForm";
import { PHONES, EMAILS } from "../lib/contact";

const offices = [
  {
    icon: FaBuilding,
    title: "Head Office",
    lines: [
      "Plot No. A, 169, Khairane Rd,",
      "Dhirubhai Ambani Knowledge City, MIDC Industrial Area,",
      "Kopar Khairane, Navi Mumbai, Maharashtra 400710",
    ],
    phones: PHONES,
  },
  {
    icon: FaMapMarkerAlt,
    title: "Bureau Office",
    lines: ["Sai nagar Mamurdi, Dehu road,", "Surve no 15/8/5 Dist Pune-412201"],
  },
  {
    icon: FaIndustry,
    title: "Factory Workshop",
    lines: [
      "Ground Floor, Plot No. A-169, TTC MIDC Khairane,",
      "Near Mahanagar CNG Gas Pump, Navi Mumbai 400710",
    ],
  },
];

const emptyForm = { name: "", company: "", phone: "", email: "", message: "", website: "" };
const fieldOrder = ["name", "company", "phone", "email", "message"];

const ContactUs = () => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const fieldError = (name, values) => validateContact(values).errors[name];

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = { ...form, [name]: value };
    setForm(next);
    if (touched[name]) setErrors((prev) => ({ ...prev, [name]: fieldError(name, next) }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: fieldError(name, form) }));
  };

  const focusFirstError = (errs) => {
    const first = fieldOrder.find((f) => errs[f]);
    if (first) document.getElementById(`contact-${first}`)?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting") return;

    const { errors: errs, valid } = validateContact(form);
    setTouched(Object.fromEntries(fieldOrder.map((f) => [f, true])));
    setErrors(errs);
    if (!valid) {
      setStatus("error");
      setMessage("Please fix the highlighted fields.");
      focusFirstError(errs);
      return;
    }

    setStatus("submitting");
    setMessage("Sending your enquiry…");
    const result = await submitForm("/api/contact", form);

    if (result.ok) {
      setForm(emptyForm);
      setTouched({});
      setErrors({});
      setStatus("success");
      setMessage("Thank you! Your enquiry has been sent. We'll get back to you shortly.");
    } else {
      setErrors(result.fieldErrors);
      setStatus("error");
      setMessage(result.error);
      focusFirstError(result.fieldErrors);
    }
  };

  const control = (name, extra = {}) => ({
    id: `contact-${name}`,
    name,
    value: form[name],
    onChange: handleChange,
    onBlur: handleBlur,
    "aria-invalid": errors[name] ? true : undefined,
    "aria-describedby": `contact-${name}-error`,
    className: inputClass(Boolean(errors[name])),
    ...extra,
  });

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-10">
        {/* Left Side - Address Information */}
        <div className="lg:col-span-2 space-y-4">
          {offices.map(({ icon: Icon, title, lines, phones }) => (
            <div
              key={title}
              className="flex gap-4 bg-white p-5 sm:p-6 rounded-2xl shadow-md border border-gray-100 border-l-4 border-l-brand-yellow"
            >
              <div className="shrink-0 w-11 h-11 rounded-xl bg-brand-navy text-brand-yellow flex items-center justify-center">
                <Icon aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-brand-navy">{title}</h2>
                <p className="mt-1 text-sm sm:text-base text-gray-600">
                  {lines.map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </p>
                {phones && (
                  <div className="mt-1 flex flex-col">
                    {phones.map((p) => (
                      <a
                        key={p.tel}
                        href={`tel:${p.tel}`}
                        className="inline-flex items-center gap-2 py-1.5 text-sm sm:text-base font-semibold text-brand-navy hover:underline"
                      >
                        <FaPhone aria-hidden="true" className="text-xs" /> {p.display}
                        <span className="text-xs font-medium text-gray-500">{p.label}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div className="flex gap-4 bg-white p-5 sm:p-6 rounded-2xl shadow-md border border-gray-100 border-l-4 border-l-brand-yellow">
            <div className="shrink-0 w-11 h-11 rounded-xl bg-brand-navy text-brand-yellow flex items-center justify-center">
              <FaEnvelope aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-bold text-brand-navy">Email</h2>
              {EMAILS.map((email) => (
                <a
                  key={email}
                  href={`mailto:${email}`}
                  className="block py-1 text-sm sm:text-base text-gray-600 hover:text-brand-navy break-words"
                >
                  {email}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Contact Form */}
        <div className="lg:col-span-3 bg-white p-6 sm:p-8 lg:p-10 rounded-3xl shadow-xl border border-gray-100">
          <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-brand-navy-light">
            Send an Enquiry
          </span>
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-brand-navy">Get In Touch</h2>
          <div className="mt-4 mb-6 h-1 w-14 rounded-full bg-brand-yellow" />

          <form onSubmit={handleSubmit} noValidate className="relative">
            <Honeypot value={form.website} onChange={handleChange} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-1">
              <FormField id="contact-name" label="Contact Person Name" required error={errors.name}>
                <input type="text" autoComplete="name" {...control("name")} />
              </FormField>
              <FormField id="contact-company" label="Company Name" error={errors.company}>
                <input type="text" autoComplete="organization" {...control("company")} />
              </FormField>
              <FormField id="contact-phone" label="Telephone No." required error={errors.phone}>
                <input type="tel" autoComplete="tel" inputMode="tel" {...control("phone")} />
              </FormField>
              <FormField id="contact-email" label="Email" required error={errors.email}>
                <input type="email" autoComplete="email" {...control("email")} />
              </FormField>
              <div className="sm:col-span-2">
                <FormField id="contact-message" label="Enquiry" required error={errors.message}>
                  <textarea rows="5" {...control("message")} />
                </FormField>
              </div>
            </div>

            <FormStatus status={status} message={message} />
            <SubmitButton submitting={status === "submitting"} submittingLabel="Sending…">
              Submit
            </SubmitButton>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;
